import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { Widget } from "./App";
import { useStore, useContactLensesStore } from "@store/index";
import type { WidgetConfig } from "@/types/index";

/**
 * PrescriptionWidget - Embedable prescription management widget
 *
 * @example
 * ```javascript
 * const widget = new PrescriptionWidget({
 *   containerId: 'widget-container',
 * });
 *
 * widget.render();
 * ```
 */
export class CLConfiguratorWidget {
  private config: WidgetConfig;
  private container: HTMLElement | null = null;
  private root: Root | null = null;
  private isInitialized = false;
  private containerObserver: MutationObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = this.validateConfig(config);
    this.init();
  }

  /**
   * Validate and merge configuration with defaults
   */
  private validateConfig(config: WidgetConfig): WidgetConfig {
    if (!config.containerId) {
      throw new Error("PrescriptionWidget: containerId is required");
    }

    return {
      locale: "en-US",
      ...config,
    };
  }

  /**
   * Initialize the widget
   */
  private init(): void {
    try {
      const state = useStore.getState();
      const contactLensesState = useContactLensesStore.getState();
      // Store configuration in Zustand store
      console.log("config", this.config);
      state.actions.initializeStore(this.config);
      // ContactLenses Store initialization
      contactLensesState.actions.setContactLensesData(
        this.config.contactLensesData,
      );

      this.isInitialized = true;
    } catch (error) {
      console.error("PrescriptionWidget initialization error:", error);
      throw error;
    }
  }

  /**
   * Render the widget in the specified container
   */
  public render(): void {
    if (!this.isInitialized) {
      throw new Error("PrescriptionWidget: Widget is not initialized");
    }

    // Get container element
    this.container = document.getElementById(this.config.containerId);

    if (!this.container) {
      throw new Error(
        `PrescriptionWidget: Container with id "${this.config.containerId}" not found`,
      );
    }

    // Guard: if already mounted, don't create a second root
    if (this.root) {
      console.warn("CLConfiguratorWidget: already rendered, skipping.");
      return;
    }

    // Create React root and render
    this.root = createRoot(this.container);
    this.root.render(<Widget />);

    // Store active instance for internal use (e.g. close from Header)
    if (typeof window !== "undefined") {
      (window as unknown as Record<string, unknown>).__clWidgetInstance = this;

      // Auto-cleanup on SPA navigation: detect container removal from DOM
      this.containerObserver = new MutationObserver(() => {
        if (this.container && !document.body.contains(this.container)) {
          this.close();
        }
      });
      this.containerObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Destroy the widget and clean up resources
   */
  public close(): void {
    console.log("PrescriptionWidget.close() called");

    if (this.containerObserver) {
      this.containerObserver.disconnect();
      this.containerObserver = null;
    }

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.container) {
      this.container.innerHTML = "";
      this.container = null;
    }

    // Reset store state
    useStore.getState().actions.resetState();
    useContactLensesStore.getState().actions.reset();

    this.isInitialized = false;

    if (typeof window !== "undefined") {
      (window as unknown as Record<string, unknown>).__clWidgetInstance = null;
    }
  }
}

// Export types for TypeScript users
export type { WidgetConfig, StepType } from "@/types";

// Make the class available globally so consumers can call: new CLConfiguratorWidget(config)
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).CLConfiguratorWidget =
    CLConfiguratorWidget;
}
